import { useMutation } from "@apollo/client";
import { TiArrowSortedDown, TiArrowSortedUp } from "react-icons/ti";
import { HiOutlineSparkles, HiOutlineLightBulb } from "react-icons/hi2";
import { FiZap } from "react-icons/fi";
import { useGlobalContext } from "../context/AuthContext";
import {
  CREATE_ACTION,
  GET_ALL_GISTS,
  UPDATE_EDIT,
} from "../../services/graphql/queriesMutations";

interface CountProps {
  label: string;
  count?: number;
  editId: number;
}

type VoteConfig = {
  shortLabel: string;
  displayLabel: string;
  icon: JSX.Element;
  field: "newnessCount" | "importantCount" | "qualityCount";
  iconClassName: string;
};

const VOTE_CONFIG: Record<string, VoteConfig> = {
  N: {
    shortLabel: "N",
    displayLabel: "NEWNESS",
    icon: <HiOutlineSparkles />,
    field: "newnessCount",
    iconClassName: "is-newness",
  },
  I: {
    shortLabel: "I",
    displayLabel: "IMPORTANCE",
    icon: <HiOutlineLightBulb />,
    field: "importantCount",
    iconClassName: "is-importance",
  },
  Q: {
    shortLabel: "Q",
    displayLabel: "QUALITY",
    icon: <FiZap />,
    field: "qualityCount",
    iconClassName: "is-quality",
  },
};

const CountComponent = ({ label, count = 0, editId }: CountProps) => {
  const config = VOTE_CONFIG[label] || VOTE_CONFIG.Q;

  const [updateEdit] = useMutation(UPDATE_EDIT, {
    refetchQueries: [{ query: GET_ALL_GISTS }],
  });

  const [createAction] = useMutation(CREATE_ACTION);
  const { user } = useGlobalContext();

  const updateField = async (
    value: number,
    actionType: "increment" | "decrement"
  ) => {
    if (!user) return;

    try {
      const actionResponse = await createAction({
        variables: {
          action: {
            editId,
            field: config.field,
            actionType,
          },
        },
      });

      if (!actionResponse) {
        return;
      }

      await updateEdit({
        variables: {
          id: editId,
          edit: {
            [config.field]: value,
          },
        },
      });
    } catch (e) {
      console.error("Failed to update counter", e);
    }
  };

  const handleIncrement = async () => {
    await updateField(count + 1, "increment");
  };

  const handleDecrement = async () => {
    await updateField(count - 1, "decrement");
  };

  return (
    <div className={`gist-vote-pill vote-${config.field}`}>
      <div className={`gist-vote-icon ${config.iconClassName}`}>{config.icon}</div>

      <div className="gist-vote-copy">
        <span className="gist-vote-label">{config.displayLabel}</span>
        <span className="gist-vote-value">{count}</span>
      </div>

      <div className="gist-vote-controls" aria-label={`${config.shortLabel} controls`}>
        <button onClick={handleIncrement} className="gist-vote-arrow" type="button" title="Increase">
          <TiArrowSortedUp />
        </button>
        <button onClick={handleDecrement} className="gist-vote-arrow" type="button" title="Decrease">
          <TiArrowSortedDown />
        </button>
      </div>
    </div>
  );
};

export default CountComponent;
